import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Trash2, RefreshCw, Building2, CheckCircle2, Clock, Plus, CreditCard } from 'lucide-react';

export default function BankStatements() {
    const { API } = useContext(AuthContext);
    const [movements, setMovements] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // New account dialog
    const [showNewAccountDialog, setShowNewAccountDialog] = useState(false);
    const [newAccount, setNewAccount] = useState({
        bank_name: 'Banco Estado',
        account_number: '',
        account_type: 'Cuenta Corriente'
    });

    const banks = [
        'Banco Estado',
        'Banco de Chile',
        'Banco Santander',
        'BCI',
        'Banco BICE',
        'Banco Itaú',
        'Scotiabank',
        'Banco Security',
        'Otro'
    ];

    const accountTypes = ['Cuenta Corriente', 'Cuenta Vista', 'Cuenta de Ahorro'];

    useEffect(() => {
        loadBankAccounts();
    }, []);

    useEffect(() => {
        if (selectedAccountId) {
            loadMovements(selectedAccountId);
        } else {
            setMovements([]);
        }
    }, [selectedAccountId]);

    const loadBankAccounts = async () => {
        try {
            const response = await axios.get(`${API}/bank/accounts`);
            const accounts = Array.isArray(response.data) ? response.data : [];
            setBankAccounts(accounts);
            // Auto-select first account if available
            if (accounts.length > 0 && !selectedAccountId) {
                setSelectedAccountId(accounts[0].id);
            }
        } catch (error) {
            console.error('Error loading bank accounts:', error);
        }
    };

    const loadMovements = async (accountId) => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/bank/movements?account_id=${accountId}&limit=20`);
            setMovements(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            toast.error('Error al cargar movimientos bancarios');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAccount = async () => {
        if (!newAccount.account_number.trim()) {
            toast.error('Ingrese el número de cuenta');
            return;
        }
        try {
            await axios.post(`${API}/bank/accounts`, newAccount);
            toast.success('Cuenta bancaria creada');
            setShowNewAccountDialog(false);
            setNewAccount({ bank_name: 'Banco Estado', account_number: '', account_type: 'Cuenta Corriente' });
            loadBankAccounts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al crear cuenta');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            toast.error('Por favor seleccione un archivo CSV');
            return;
        }

        if (!selectedAccountId) {
            toast.error('Seleccione una cuenta bancaria primero');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('bank_account_id', selectedAccountId);

            const response = await axios.post(`${API}/bank/import`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const result = response.data;
            if (result.duplicates > 0) {
                toast.success(`${result.imported} movimientos importados, ${result.duplicates} duplicados omitidos`);
            } else {
                toast.success(`${result.imported} movimientos importados`);
            }
            loadMovements(selectedAccountId);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al cargar archivo');
            console.error(error);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeleteAccount = async (id) => {
        if (!confirm('¿Eliminar esta cuenta bancaria y todos sus movimientos?')) return;
        try {
            await axios.delete(`${API}/bank/accounts/${id}`);
            toast.success('Cuenta eliminada');
            if (selectedAccountId === id) setSelectedAccountId('');
            loadBankAccounts();
        } catch (error) {
            toast.error('Error al eliminar cuenta');
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const selectedAccount = bankAccounts.find(a => a.id === selectedAccountId);
    const stats = {
        total: movements.length,
        pending: movements.filter(m => m.status === 'pending' || m.match_status === 'pending').length,
        matched: movements.filter(m => m.status === 'matched' || m.match_status === 'confirmed').length
    };

    return (
        <div className="p-8 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-2">
                        <Building2 className="h-8 w-8 text-primary" />
                        Cartola Bancaria
                    </h1>
                    <p className="text-muted-foreground">Gestiona tus cuentas y movimientos bancarios.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => selectedAccountId && loadMovements(selectedAccountId)}>
                        <RefreshCw className="mr-2 h-4 w-4" /> Recargar
                    </Button>
                    <Button onClick={() => setShowNewAccountDialog(true)}>
                        <Plus className="mr-2 h-4 w-4" /> Nueva Cuenta
                    </Button>
                </div>
            </div>

            {/* Bank Accounts Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {bankAccounts.map(account => (
                    <Card
                        key={account.id}
                        className={`cursor-pointer transition-all ${selectedAccountId === account.id
                                ? 'ring-2 ring-primary border-primary'
                                : 'hover:border-primary/50'
                            }`}
                        onClick={() => setSelectedAccountId(account.id)}
                    >
                        <CardContent className="pt-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <CreditCard className="h-4 w-4 text-primary" />
                                        <span className="font-semibold">{account.bank_name}</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {account.account_type}
                                    </p>
                                    <p className="font-mono text-sm">{account.account_number}</p>
                                </div>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteAccount(account.id); }}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}

                {bankAccounts.length === 0 && (
                    <Card className="col-span-full border-dashed">
                        <CardContent className="py-8 text-center text-muted-foreground">
                            <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>No hay cuentas bancarias registradas.</p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => setShowNewAccountDialog(true)}
                            >
                                <Plus className="mr-2 h-4 w-4" /> Crear primera cuenta
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Upload Section - Only show if account selected */}
            {selectedAccountId && (
                <Card className="border-dashed">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Upload className="h-5 w-5" />
                            Cargar Cartola - {selectedAccount?.bank_name} ({selectedAccount?.account_number})
                        </CardTitle>
                        <CardDescription>
                            Sube un archivo CSV. Solo se importarán movimientos nuevos.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex gap-4 items-end">
                            <div className="grid gap-1.5 flex-1">
                                <Label>Archivo CSV</Label>
                                <Input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileUpload}
                                    disabled={uploading}
                                />
                            </div>
                            {uploading && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                    Procesando...
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Stats */}
            {selectedAccountId && movements.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold">{stats.total}</div>
                            <p className="text-muted-foreground text-sm">Movimientos (últimos 20)</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6 flex items-center gap-2">
                            <Clock className="h-5 w-5 text-yellow-500" />
                            <div>
                                <div className="text-2xl font-bold">{stats.pending}</div>
                                <p className="text-muted-foreground text-sm">Pendientes</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6 flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <div>
                                <div className="text-2xl font-bold">{stats.matched}</div>
                                <p className="text-muted-foreground text-sm">Conciliados</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Movements Table */}
            {selectedAccountId && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5" />
                            Movimientos - {selectedAccount?.bank_name}
                            <span className="text-sm font-normal text-muted-foreground">
                                (últimos 20)
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="py-8 text-center text-muted-foreground">
                                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                                Cargando...
                            </div>
                        ) : movements.length === 0 ? (
                            <div className="py-8 text-center text-muted-foreground">
                                <p>No hay movimientos para esta cuenta.</p>
                                <p className="text-sm">Sube una cartola CSV para comenzar.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left p-2">Fecha</th>
                                            <th className="text-left p-2">Descripción</th>
                                            <th className="text-right p-2">Cargo</th>
                                            <th className="text-right p-2">Abono</th>
                                            <th className="text-right p-2">Saldo</th>
                                            <th className="text-center p-2">Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {movements.map((m) => (
                                            <tr key={m.id} className="border-b hover:bg-secondary/30">
                                                <td className="p-2 font-mono text-xs">{m.date}</td>
                                                <td className="p-2 max-w-[300px] truncate">{m.description}</td>
                                                <td className="p-2 text-right text-red-600 font-mono">
                                                    {m.debit > 0 ? formatCurrency(m.debit) : '-'}
                                                </td>
                                                <td className="p-2 text-right text-green-600 font-mono">
                                                    {m.credit > 0 ? formatCurrency(m.credit) : '-'}
                                                </td>
                                                <td className="p-2 text-right font-mono font-medium">
                                                    {formatCurrency(m.balance)}
                                                </td>
                                                <td className="p-2 text-center">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${(m.match_status === 'confirmed' || m.status === 'matched')
                                                            ? 'bg-green-100 text-green-800'
                                                            : m.match_status === 'proposed'
                                                                ? 'bg-blue-100 text-blue-800'
                                                                : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        {m.match_status === 'confirmed' || m.status === 'matched'
                                                            ? 'Conciliado'
                                                            : m.match_status === 'proposed'
                                                                ? 'Propuesto'
                                                                : 'Pendiente'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* New Account Dialog */}
            <Dialog open={showNewAccountDialog} onOpenChange={setShowNewAccountDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Nueva Cuenta Bancaria</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid gap-2">
                            <Label>Banco</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                                value={newAccount.bank_name}
                                onChange={(e) => setNewAccount({ ...newAccount, bank_name: e.target.value })}
                            >
                                {banks.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                        <div className="grid gap-2">
                            <Label>Tipo de Cuenta</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                                value={newAccount.account_type}
                                onChange={(e) => setNewAccount({ ...newAccount, account_type: e.target.value })}
                            >
                                {accountTypes.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="grid gap-2">
                            <Label>Número de Cuenta</Label>
                            <Input
                                placeholder="Ej: 123456789"
                                value={newAccount.account_number}
                                onChange={(e) => setNewAccount({ ...newAccount, account_number: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNewAccountDialog(false)}>Cancelar</Button>
                        <Button onClick={handleCreateAccount}>Crear Cuenta</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
